import * as React from 'react';
import { useState, createContext, useContext} from 'react';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import PaymentForm from './subForms/PaymentForm';
import ReviewForm from './subForms/ReviewForm';
import InformationForm from './subForms/InformationForm';
import EmergencyForm from './subForms/EmergencyForm';
import moment from 'moment';
import WaiverForm from './subForms/WaiverForm';
import CampWaiverForm from './subForms/CampWaiverForm';
import FormStepper from './components/FormStepper';
import SubForm from './subForms/SubForm';
import TShirtForm from './subForms/TShirtForm';
import { useParams } from './components/ParamsContext';
import { useConferenceConfig } from './components/ConferenceConfigContext';
import ServicesForm from './subForms/ServicesForm';

function Copyright() {
  return (
    <Typography variant="body2" color="text.secondary" align="center">
      Contact us: <a href="mailto:norcalchristianstudents@gmail.com">norcalchristianstudents@gmail.com</a>
    </Typography>
  );
}

// Every step the form could show. Which ones actually appear for a given
// conference comes from conferences/{conferenceId}.enabledSteps, instead of
// commenting lines in/out here.
const STEP_DEFINITIONS = {
  information: { label: 'Personal Information', component: InformationForm },
  emergency: { label: 'Emergency Contact', component: EmergencyForm },
  waiver: { label: 'Release of Liability', component: WaiverForm },
  campWaiver: { label: 'Camp Waiver', component: CampWaiverForm },
  services: { label: 'Services', component: ServicesForm },
  merch: { label: 'Merchandise', component: TShirtForm },
  review: { label: 'Review', component: ReviewForm },
  payment: { label: 'Payment', component: PaymentForm },
};

// Maps the `parttime` query param (a link-specific override, see
// ParamsContext) to the matching key in conferences/{conferenceId}.products.
// The default full-time case (no parttime param) isn't in this map - its
// price depends on the registrant's status, resolved once InformationForm
// collects it (see resolveFullTimeProducts below).
export const PRODUCT_KEY_BY_PARAM = {
  'saturday-afternoon': 'SAT-AFTERNOON',
  'saturday-evening': 'SAT-EVENING',
  'saturday-only': 'SATURDAY-ONLY',
  'lords-day': 'LORDS-DAY',
};

// Friendly names for the special-link participation types, keyed by the same
// product key as PRODUCT_KEY_BY_PARAM above - shared by the public form
// (heading under the title, so a registrant knows which link they used) and
// ConferenceSettingsPanel (checkbox labels), so they can never drift apart.
export const PARTTIME_LINK_LABELS = {
  'SAT-AFTERNOON': 'Saturday Afternoon Only',
  'SAT-EVENING': 'Saturday Evening Only',
  'SATURDAY-ONLY': 'Saturday Only',
  'LORDS-DAY': "Lord's Day Only",
};

// Which InformationForm `status` values count as "student" for full-time
// pricing purposes - everything else (working professionals, Serving Ones,
// recent grads, etc.) gets non-student pricing.
export const STUDENT_STATUSES = ['High School Senior', 'Freshman', 'Sophomore', 'Junior', 'Senior', 'Grad Student'];

function applyLateFee(products, config, noLateFee) {
  if (!(moment().isAfter(config.regularDeadline) && !noLateFee && config.lateFeeProduct)) {
    return products;
  }
  const hasLateFee = products.some(obj => obj['name'] === 'Late Fee')
  return hasLateFee ? products : [...products, { ...config.lateFeeProduct }]
}

// Resolves the correct full-time product (student vs non-student pricing)
// once the registrant's status is known, and applies the late fee the same
// way the link-based participation types do at the top of Form().
export function resolveFullTimeProducts(status, config, noLateFee) {
  const productKey = STUDENT_STATUSES.includes(status) ? 'FT-STUDENT' : 'FT-NONSTUDENT'
  const productConfig = config.products?.[productKey]
  const products = productConfig ? [{ ...productConfig }] : []
  return { participation: productKey, products: applyLateFee(products, config, noLateFee) }
}

// Create StepContext
const DataContext = createContext();

// StepProvider Component to provide step-related values and functions
const DataProvider = ({ children, defaultData}) => {
  const [data, setData] = useState(defaultData);

  const updateData = (newData) => {
    setData({ ...data, ...newData })
  };

  const clearData = () => {
    setData(defaultData)
  };

  return (
    <DataContext.Provider value={{ data, updateData, clearData }}>
      {children}
    </DataContext.Provider>
  );
};

// Custom Hook for consuming the StepContext
const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useStep must be used within a StepProvider');
  }
  return context;
};

// Create StepContext
const StepContext = createContext();

// StepProvider Component to provide step-related values and functions
const StepProvider = ({ children, totalSteps }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const incrementStep = () => {
    setCurrentStep(prevStep => Math.min(prevStep + 1, totalSteps));
  };

  const decrementStep = () => {
    setCurrentStep(prevStep => Math.max(prevStep - 1, 0));
  };

  const firstStep= () => {
    setCurrentStep(0)
  }

  const isLastStep = currentStep === totalSteps;

  return (
    <StepContext.Provider value={{ currentStep, incrementStep, decrementStep, firstStep, isLastStep }}>
      {children}
    </StepContext.Provider>
  );
};

// Custom Hook for consuming the StepContext
const useStep = () => {
  const context = useContext(StepContext);
  if (!context) {
    throw new Error('useStep must be used within a StepProvider');
  }
  return context;
};


export { DataProvider, useData, StepProvider, useStep };


export function Form() {
  const {discount, noLateFee, partTime} = useParams()
  const { conferenceId, config } = useConferenceConfig()

  const steps = (config.enabledSteps || []).map((key) => STEP_DEFINITIONS[key])

  // Full time (no parttime link, or a disabled/unrecognized one) is priced by
  // student/non-student status, which isn't known yet - InformationForm
  // resolves the real products once the registrant submits it. Every other
  // participation type is fully determined by the link itself, so it's
  // resolved immediately here. enabledPartTimeOptions lets an admin turn a
  // special link off without deleting its price - a disabled link just falls
  // back to the normal full-time flow instead of erroring.
  const mappedPartTimeKey = PRODUCT_KEY_BY_PARAM[partTime]
  const isPartTimeLinkActive = mappedPartTimeKey && (config.enabledPartTimeOptions || []).includes(mappedPartTimeKey)
  const isDeferredFullTime = !isPartTimeLinkActive
  const participation = isDeferredFullTime ? 'FT' : mappedPartTimeKey
  // Only shown for an active special link - a disabled/unrecognized one
  // silently falls back to the normal full-time flow (see isDeferredFullTime
  // above), so no heading should appear for it either.
  const activeLinkLabel = isPartTimeLinkActive ? (PARTTIME_LINK_LABELS[mappedPartTimeKey] || mappedPartTimeKey) : null
  let products = []
  if (!isDeferredFullTime) {
    const productConfig = config.products?.[participation]
    products = applyLateFee(productConfig ? [{ ...productConfig }] : [], config, noLateFee)
  }

  const discountConfig = discount ? config.discountCodes?.[discount] : null
  const discountData = discountConfig ? { hasFinancialSupport: true, ...discountConfig } : { hasFinancialSupport: false }

  const formData = {
    orderID: '',
    ...discountData,
    products,
    participation,
    conferenceId,
  }

  return (
    <StepProvider totalSteps={steps.length}>
      <DataProvider defaultData = {formData}>
    <React.Fragment>
      <Container component="form" maxWidth="md" sx={{ mb: 4 }}>
        <Paper elevation={1} sx={{ my: { xs: 3, md: 5 }, p: { xs: 3, md: 5 } }}>
          <Typography component="h1" variant="h4" align="center" fontWeight={700} gutterBottom>
            Registration Form
          </Typography>
          {activeLinkLabel && (
            <Typography variant="h6" align="center" color="text.secondary" gutterBottom>
              {activeLinkLabel}
            </Typography>
          )}
          <FormStepper steps={steps}/>
          <SubForm steps={steps}/>
        </Paper>
        <Copyright />
      </Container>
    </React.Fragment>
    </DataProvider>
    </StepProvider>
  );
}

